import { isEmpty } from 'lodash';

import { getOperationPropertyKeys } from '@/explore/pipeline/operation';
import { isCellWithPipeline, replaceCell, setCellPipeline, restoreInvalidCell } from '@/core/cell';
import { isPipelineChildOf } from '@/core/pipeline';
import { getExplorationCell } from '@/core/exploration';

import {
  AddRelatedColumnOperation,
  Exploration,
  PipelineOperation,
  RelationAggregateOperation,
  Pipeline,
  CellWithPipeline,
} from '../../types';
import { getAllPipelineFields, getFinalState, PipelineStateContext } from '../../pipeline/state';
import { ensureValidPipeline, getCellPipeline, restoreInvalidOperation } from '../../utils';

export const ensureLegalIdentifier = (name: string) => {
  return name.replace(/[^a-zA-Z0-9_]/g, '_').replace(/^([^a-zA-Z])/, '_$1');
};

/**
 * Make names safe-ish as column keys
 */
export const nameToKey = (name: string) => {
  return ensureLegalIdentifier(name).toLowerCase();
};

export const isRecursiveOperation = (
  operation: PipelineOperation,
): operation is AddRelatedColumnOperation | RelationAggregateOperation =>
  operation.operation === 'addRelatedColumn' || operation.operation === 'relationAggregate';

export const adjustFieldOrder = (
  cell: CellWithPipeline,
  updatedPipeline: Pipeline,
  ctx: PipelineStateContext,
) => {
  const existingPipeline = getCellPipeline(cell);
  if (existingPipeline === undefined) {
    throw new Error('Attempting to update an operation on a cell without a pipeline');
  }

  const fieldOrder = cell.viewOptions?.fieldOrder;
  if (isEmpty(fieldOrder)) {
    return cell;
  }

  const previousState = getFinalState(existingPipeline, ctx);
  const nextState = getFinalState(updatedPipeline, ctx);
  const previousKeys = previousState.fields.map((f) => f.key);
  const nextKeys = nextState.fields.map((f) => f.key);

  const previousUnpositionedKeys = previousKeys.filter((key) => !(key in fieldOrder));
  const nextUnpositionedKeys = nextKeys.filter((key) => !(key in fieldOrder));
  const addedKeys = nextUnpositionedKeys.filter((key) => !previousUnpositionedKeys.includes(key));

  const newFieldOrder: Record<string, number> = Object.entries(fieldOrder).reduce(
    (acc, [key, index], keyIndex) => {
      const previousPrecedingCount = index - keyIndex;
      const previousPrecedingKeys = previousUnpositionedKeys.slice(0, previousPrecedingCount);
      const nextPrecedingCount = nextUnpositionedKeys.findIndex(
        (k) => !previousPrecedingKeys.includes(k) && !addedKeys.includes(k),
      );

      // Let new fields at the positioned index come after the positioned field
      const nextPrecedingCountAdjusted =
        nextUnpositionedKeys.findLastIndex(
          (k, i) => !addedKeys.includes(k) && i < nextPrecedingCount,
        ) + 1;

      const precedingCountAfter = nextPrecedingCountAdjusted + keyIndex;
      return {
        ...acc,
        [key]: precedingCountAfter,
      };
    },
    {},
  );

  return {
    ...cell,
    viewOptions: {
      ...cell.viewOptions,
      fieldOrder: newFieldOrder,
    },
  };
};

export const updateCellPipeline = (
  exploration: Exploration,
  cellId: string,
  pipeline: Pipeline,
  ctx: PipelineStateContext,
): Exploration => {
  const cell = getExplorationCell(exploration, cellId);
  if (cell === undefined) {
    throw new Error('Cell not found');
  }
  const restoredCell = restoreInvalidCell(cell);
  if (!isCellWithPipeline(restoredCell)) {
    throw new Error('Attempting to update an operation on a cell without a pipeline');
  }
  return {
    ...exploration,
    view: {
      ...exploration.view,
      cells: replaceCell(
        exploration.view.cells,
        setCellPipeline(adjustFieldOrder(restoredCell, pipeline, ctx), pipeline),
        restoredCell.id,
      ),
    },
  };
};

export const replaceOperations = (
  exploration: Exploration,
  cellId: string,
  operations: PipelineOperation[],
  ctx: PipelineStateContext,
) => {
  const cell = getExplorationCell(exploration, cellId);
  if (cell === undefined) {
    throw new Error('Cell not found');
  }
  const pipeline = getCellPipeline(cell);
  if (pipeline === undefined) {
    throw new Error('Attempting to update an operation on a cell without a pipeline');
  }
  return updateCellPipeline(exploration, cell.id, { ...pipeline, operations }, ctx);
};

export const getDescendantPipelines = (
  parentPipeline: Pipeline,
  exploration: Exploration,
): Pipeline[] => {
  return exploration.view.cells.flatMap((cell) => {
    const pipeline = getCellPipeline(cell);
    const isChild = pipeline !== undefined && isPipelineChildOf(pipeline, parentPipeline);
    if (isChild) {
      return [pipeline, ...getDescendantPipelines(pipeline, exploration)];
    }
    return [];
  });
};

const applyOperationToPipeline = (
  exploration: Exploration,
  cellId: string,
  operationIndex: number,
  operation: PipelineOperation,
  ctx: PipelineStateContext,
  replace?: boolean,
): Exploration => {
  const cell = getExplorationCell(exploration, cellId);
  if (cell === undefined) {
    throw new Error('Cell not found');
  }
  const pipeline = getCellPipeline(cell);
  if (pipeline === undefined) {
    throw new Error('Attempting to update an operation on a cell without a pipeline');
  }

  const affectedPipelines = [pipeline, ...getDescendantPipelines(pipeline, exploration)];
  const validAffectedPipelines = affectedPipelines.map((pipeline) =>
    ensureValidPipeline(pipeline, {
      models: ctx.models,
      metrics: ctx.metrics,
      variables: ctx.variables,
      exploration,
    }),
  );

  operation = ensureUniquePropertyKeysInOperation(
    restoreInvalidOperation(operation),
    validAffectedPipelines,
    ctx,
    replace === true ? pipeline.operations.at(operationIndex) : undefined,
  );

  const operations = pipeline.operations;
  const before = operations.slice(0, operationIndex);
  const after = operations.slice(operationIndex + (replace === true ? 1 : 0));
  return replaceOperations(exploration, cell.id, [...before, operation, ...after], ctx);
};

export const addOperation = (
  exploration: Exploration,
  cellId: string,
  operationIndex: number,
  operation: PipelineOperation,
  ctx: PipelineStateContext,
): Exploration => {
  return applyOperationToPipeline(exploration, cellId, operationIndex, operation, ctx);
};

export const addOperations = (
  exploration: Exploration,
  cellId: string,
  operationIndex: number,
  operations: PipelineOperation[],
  ctx: PipelineStateContext,
): Exploration => {
  return operations.reduce(
    (exploration, operation, idx) =>
      addOperation(exploration, cellId, operationIndex + idx, operation, ctx),
    exploration,
  );
};

export const updateOperation = (
  exploration: Exploration,
  cellId: string,
  operationIndex: number,
  operation: PipelineOperation,
  ctx: PipelineStateContext,
): Exploration => {
  return applyOperationToPipeline(exploration, cellId, operationIndex, operation, ctx, true);
};

export const removeOperation = (
  exploration: Exploration,
  cellId: string,
  operationIndex: number,
  ctx: PipelineStateContext,
): Exploration => {
  const cell = getExplorationCell(exploration, cellId);
  if (cell === undefined) {
    throw new Error('Cell not found');
  }
  const pipeline = getCellPipeline(cell);
  if (pipeline === undefined) {
    throw new Error('Attempting to remove an operation from a cell without a pipeline');
  }

  return replaceOperations(
    exploration,
    cellId,
    [
      ...pipeline.operations.slice(0, operationIndex),
      ...pipeline.operations.slice(operationIndex + 1),
    ],
    ctx,
  );
};

// TODO: move to core
export const moveOperation = (
  exploration: Exploration,
  cellId: string,
  fromIndex: number,
  toIndex: number,
  ctx: PipelineStateContext,
): Exploration => {
  const cell = getExplorationCell(exploration, cellId);
  if (cell === undefined) {
    throw new Error('Cell not found');
  }
  const pipeline = getCellPipeline(cell);
  if (pipeline === undefined) {
    throw new Error('Attempting to remove an operation from a cell without a pipeline');
  }

  const tempPipeline = [
    ...pipeline.operations.slice(0, fromIndex),
    ...pipeline.operations.slice(fromIndex + 1),
  ];

  return replaceOperations(
    exploration,
    cell.id,
    [
      ...tempPipeline.slice(0, toIndex),
      pipeline.operations[fromIndex],
      ...tempPipeline.slice(toIndex),
    ],
    ctx,
  );
};

// Change the key argument so it does not conflict with existing keys in the fields.
export const ensureUniqueFieldKey = (fields: { key: string }[], key: string) =>
  ensureUniqueKey(
    fields.map((field) => field.key),
    key,
  );

/**
 * Ensure that the key is unique among the existing keys.
 */
export const ensureUniqueKey = (existingKeys: string[], key: string) => {
  let result = key;
  let iteration = 0;

  while (existingKeys.includes(result) && iteration < 1000) {
    result = `${key}_${iteration}`;
    iteration += 1;
  }

  return result;
};

export const ensureUniquePropertyKeysInOperation = <T extends PipelineOperation>(
  operation: T,
  affectedPipelines: Pipeline[],
  ctx: PipelineStateContext,
  previousOperation?: T,
): T => {
  let keys = getOperationPropertyKeys(operation);
  if (keys.length === 0) {
    return operation;
  }

  const ownKeys =
    previousOperation !== undefined ? getOperationPropertyKeys(previousOperation) : [];

  const existingKeys = affectedPipelines
    .flatMap((pipeline) => getAllPipelineFields(pipeline, ctx))
    .map((field) => field.key);

  let iteration = 0;
  let conflictingKeys = keys.filter((key) => !ownKeys.includes(key) && existingKeys.includes(key));
  while ((conflictingKeys.length > 0 || hasDuplicates(keys)) && ++iteration < 1000) {
    operation = deduplicateOperationPropertyKeys(operation, conflictingKeys);
    keys = getOperationPropertyKeys(operation);
    conflictingKeys = keys.filter((key) => !ownKeys.includes(key) && existingKeys.includes(key));
  }
  return operation;
};

const hasDuplicates = (keys: string[]) => new Set(keys).size !== keys.length;

export const deduplicateOperationPropertyKeys = <T extends PipelineOperation>(
  operation: T,
  fields: string[],
): T => {
  switch (operation.operation) {
    case 'addRelatedColumn':
      return {
        ...operation,
        parameters: {
          ...operation.parameters,
          columns: ensureUniquePropertyKeys(operation.parameters.columns, fields),
        },
      };
    case 'groupAggregate':
      return {
        ...operation,
        parameters: {
          ...operation.parameters,
          aggregations: ensureUniquePropertyKeys(operation.parameters.aggregations, fields),
        },
      };
    case 'relationAggregate':
      return {
        ...operation,
        parameters: {
          ...operation.parameters,
          aggregations: ensureUniquePropertyKeys(operation.parameters.aggregations, fields),
        },
      };
    case 'deriveField':
      return {
        ...operation,
        parameters: {
          ...operation.parameters,
          key: ensureUniqueKey(fields, operation.parameters.key),
        },
      };
  }
  return operation;
};

const ensureUniquePropertyKeys = <T extends { property: { key: string } }>(
  aggregations: T[],
  fields: string[],
): T[] =>
  aggregations.reduce<T[]>((aggregations, aggregation) => {
    const keys = fields.concat(aggregations.map((agg) => agg.property.key));
    return [
      ...aggregations,
      {
        ...aggregation,
        property: {
          ...aggregation.property,
          key: ensureUniqueKey(keys, aggregation.property.key),
        },
      },
    ];
  }, []);
